import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    forwardRef,
    inject,
    OnInit,
    Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PushPipe } from '@ngrx/component';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import {
    EntityAgGridTableComponent,
    IconWithTextCellRendererComponent,
    IconWithTextCellRendererParams,
    SERVER_SIDE_DEFAULT_GRID_OPTIONS,
} from '@wdx/shared/components/ag-grid';
import {
    WdxButtonModule,
    WdxButtonSize,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxFormFieldModule } from '@wdx/shared/components/wdx-form-field';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxIconButtonModule } from '@wdx/shared/components/wdx-icon-button';
import { WdxInputGroupModule } from '@wdx/shared/components/wdx-input-group';
import { WdxInputTextModule } from '@wdx/shared/components/wdx-input-text';
import { WdxIsLoadingModule } from '@wdx/shared/components/wdx-is-loading';
import {
    FormContextualDataService,
    FormStaticService,
    IWrappedFormComponent,
} from '@wdx/shared/infrastructure/form-framework';
import {
    FilterOperator,
    FormElementStyle,
    FormFieldType,
    FULLSCREEN_MODAL_OPTIONS,
    LookupFieldResult,
    LookupObject,
    LookupSourceType,
    QueryExpression,
    SystemEntity,
    TranslateTokenisedStringPipe,
    UTILS_ICON_DELETE,
    UTILS_ICON_SEARCH,
} from '@wdx/shared/utils';
import { ColDef, GridOptions } from 'ag-grid-community';
import { map, Observable } from 'rxjs';
import { BaseReactiveFormAdapterClass } from '../../../classes/base-reactive-form-adapter.class';
import { MoleculeInfoCardModule } from '../../../components/molecules/molecule-info-card/molecule-info-card.module';
import { DynamicEntityHelperService } from '../../../features/dynamic-pages/services/dynamic-entity-helper.service';
import { InfoCardData } from '../../../models/info-card-data.model';
import { ViewsFacadeService } from '../../../state/views/views-facade.service';
import {
    ContactPointLookup,
    InfoCardService,
} from '../../services/info-card/info-card.service';
import { FormLookupSelectedItemsComponent } from './lookup-selected-items/lookup-selected-items.component';
import { FormLookupTableModalComponent } from './lookup-table-modal/form-lookup-table-modal.component';

@Component({
    selector: 'clmi-form-advanced-lookup',
    templateUrl: './form-advanced-lookup.component.html',
    styleUrls: ['./form-advanced-lookup.component.scss'],
    imports: [
        WdxIsLoadingModule,
        WdxFormFieldModule,
        WdxInputGroupModule,
        MoleculeInfoCardModule,
        WdxIconModule,
        TranslateTokenisedStringPipe,
        WdxInputTextModule,
        MoleculeInfoCardModule,
        WdxIconButtonModule,
        WdxButtonModule,
        FormLookupSelectedItemsComponent,
        PushPipe,
        EntityAgGridTableComponent,
        NgClass,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        DynamicEntityHelperService,
        PartyToAvatarPipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormAdvancedLookupComponent),
            multi: true,
        },
    ],
})
export class FormAdvancedLookupComponent
    extends BaseReactiveFormAdapterClass
    implements IWrappedFormComponent, OnInit
{
    @Output() blurred = new EventEmitter();

    private formContextualDataService = inject(FormContextualDataService);
    private formStaticService = inject(FormStaticService);
    private modalService = inject(NgbModal);
    private destroyRef = inject(DestroyRef);
    private cdr = inject(ChangeDetectorRef);
    private viewsFacade = inject(ViewsFacadeService);
    private dynamicEntityHelperService = inject(DynamicEntityHelperService);
    public infoCardService = inject(InfoCardService);
    public val: LookupObject[];
    public lookupCards: InfoCardData[];
    public columnDefs$: Observable<ColDef[]>;
    public columnDefs: ColDef[];
    public formData: Record<string, any>;

    public gridOptions: GridOptions = {
        ...SERVER_SIDE_DEFAULT_GRID_OPTIONS,
        pagination: false,
        suppressCellFocus: true,
        suppressHeaderFocus: true,
    };
    public formViewType: 'grid' | 'lookups';
    public entityType: SystemEntity;
    private layoutQueryExpressions: QueryExpression[];
    public inlineGridQuery: QueryExpression[];
    public readonly WdxButtonStyle = WdxButtonStyle;
    public readonly WdxButtonSize = WdxButtonSize;
    public readonly UTILS_ICON_DELETE = UTILS_ICON_DELETE;
    public readonly UTILS_ICON_SEARCH = UTILS_ICON_SEARCH;
    public readonly FormElementStyle = FormElementStyle;

    private modalComponent: FormLookupTableModalComponent;

    ngOnInit(): void {
        this.layoutQueryExpressions =
            this.formInputData?.lookupConfiguration?.filter?.expressions?.map(
                (exp) => ({
                    ...exp,
                    values: this.getContextualData(exp.values),
                }),
            ) ?? [];
        if (
            this.formInputData?.elementStyle === FormElementStyle.Grid &&
            this.formInputData?.lookupConfiguration?.viewCode &&
            this.formInputData?.lookupSources?.length === 1 &&
            this.formInputData?.forcesFullWidth
        ) {
            this.formViewType = 'grid';
            const lookupType = this.formInputData.lookupSources[0].type;
            this.entityType = lookupType as unknown as SystemEntity;
            const viewCode = this.formInputData?.lookupConfiguration?.viewCode;

            this.columnDefs$ = this.viewsFacade
                .getViewsForEntityType$(lookupType, undefined, {
                    isLookup: true,
                })
                .pipe(
                    map(
                        (views) =>
                            views.find((v) => v.id === viewCode) ?? views?.[0],
                    ),
                    map((view) => [
                        ...(view?.configuration?.columns?.map((col) =>
                            this.dynamicEntityHelperService.createColDef(
                                this.entityType,
                                col,
                                { disable: true },
                                [],
                            ),
                        ) || []),
                        ...(this.formInputData?.isDisabled ||
                        this.formStaticService.isReadonlyMode
                            ? []
                            : [
                                  {
                                      headerName: '',
                                      width: 35,
                                      cellRenderer:
                                          IconWithTextCellRendererComponent,
                                      cellRendererParams: {
                                          value: {
                                              icon: UTILS_ICON_DELETE,
                                              clickCallback: (data) =>
                                                  this.deleteItem({ data }),
                                          } as IconWithTextCellRendererParams,
                                      },
                                  },
                              ]),
                    ]),
                );
        } else {
            this.formViewType = 'lookups';
        }
    }

    onOpen(): void {
        if (this.formInputData?.isDisabled) {
            return;
        }

        const { componentInstance, closed, dismissed } = this.modalService.open(
            FormLookupTableModalComponent,
            FULLSCREEN_MODAL_OPTIONS,
        );
        this.modalComponent = componentInstance;

        this.modalComponent.value = this.lookupCards?.map(
            (infoCard) => infoCard.data,
        );
        this.modalComponent.formInputData = this.formInputData;
        this.modalComponent.isMulti = this.isMulti;
        this.modalComponent.formData = this.formData;
        this.modalComponent.layoutQueryExpressions =
            this.layoutQueryExpressions;

        closed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                ({
                    rows,
                }: {
                    rows?: { id: string; lookupType: LookupSourceType }[];
                }) => {
                    this.val = rows.flatMap((row) => {
                        const lookupType =
                            this.formInputData?.fieldType ===
                            FormFieldType.DocumentLookup
                                ? LookupSourceType.Document
                                : row.lookupType;
                        return this.infoCardService.convertQueryToLookupArray(
                            lookupType,
                            row,
                        );
                    });
                    this.lookupCards =
                        this.infoCardService.lookupResultsAsInfoCardData(rows);

                    if (this.formViewType === 'grid') {
                        this.updateInlineGridContents();
                    }

                    this.updateFormControl(this.val);
                    this.cdr.markForCheck();
                    this.blurred.emit();
                },
            );
        dismissed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.modalComponent = undefined;
        });
    }

    deleteItem(item: { data: LookupFieldResult | ContactPointLookup }) {
        this.removeItem(item.data);
        this.updateFormControl(this.val);
    }

    override writeValue(
        value:
            | LookupObject
            | LookupFieldResult
            | ContactPointLookup
            | Array<LookupFieldResult | ContactPointLookup | LookupObject>,
    ): void {
        this.infoCardService.getLookups$(value).subscribe((lookups) => {
            this.writeValueUpdates(lookups);
        });
    }

    private writeValueUpdates(
        value:
            | LookupFieldResult
            | ContactPointLookup
            | Array<LookupFieldResult | ContactPointLookup>,
    ) {
        this.lookupCards =
            this.infoCardService.lookupResultsAsInfoCardData(value);
        this.val = this.infoCardService.convertLookupToArray(value);
        super.writeValue(value);
        this.updateInlineGridContents();

        if (!this.modalComponent) {
            return;
        }
        this.modalComponent.value = this.val;
    }

    private removeItem(toDelete: { id?: string }) {
        if (!toDelete.id) {
            return;
        }
        this.val = this.val?.filter((item) => toDelete.id !== item.id) ?? [];
        this.lookupCards =
            this.lookupCards?.filter((item) => toDelete.id !== item.id) ?? [];
        if (this.formViewType === 'grid') {
            this.updateInlineGridContents();
        }
    }

    private updateInlineGridContents() {
        const selectedIdQuery = this.val?.length
            ? [
                  {
                      field: 'Id',
                      operator: FilterOperator.In,
                      values: this.val?.map(({ id }) => id),
                  },
              ]
            : [];

        this.inlineGridQuery = selectedIdQuery;
    }

    private updateFormControl(
        newValue: Array<LookupFieldResult | ContactPointLookup>,
    ) {
        if (this.formInputData?.fieldType === FormFieldType.LookupSingle) {
            this.onChange(newValue?.length ? newValue[0] : null);
            return;
        }
        this.onChange(newValue?.length ? newValue : null);
    }

    private getContextualData(contextualValues: string[]) {
        const { lookups, strings } =
            this.formContextualDataService.getContextualData(
                contextualValues,
                this.formStaticService?.form.getRawValue(),
            );
        return lookups?.length ? lookups : strings;
    }
}
